import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Building2,
  Target,
  Compass,
  Sparkles,
  Award,
  Leaf,
  Handshake,
  TrendingUp,
  ShieldCheck,
  Heart,
  MapPin,
  Users,
  Factory,
  Calendar,
} from "lucide-react";

const OBJECTIVES = [
  { icon: <Award className="w-5 h-5" />, title: "Champion Quality & Innovation" },
  { icon: <Heart className="w-5 h-5" />, title: "Promote Dignity & Wellness" },
  { icon: <Leaf className="w-5 h-5" />, title: "Support Sustainable Manufacturing" },
  { icon: <ShieldCheck className="w-5 h-5" />, title: "Build Trust Through Integrity & Reliability" },
  { icon: <TrendingUp className="w-5 h-5" />, title: "Drive Growth Through People, Empowerment & Learning" },
];

const DIFFERENTIATORS = [
  "Made from pure wood pulp",
  "Comfortable and absorbent",
  "Premium quality at an affordable price",
  "Manufactured in Ghana",
  "Designed with secure wings for improved protection",
  "Supports menstrual health and hygiene accessibility",
  "Available in multiple formats for different flow needs",
];

const PRODUCTS = [
  { name: "Normal Flow Pads", tag: "Daily protection" },
  { name: "Heavy Flow / Super Pads", tag: "Extra absorbency" },
  { name: "Extra Long / Night Towels", tag: "Overnight comfort" },
  { name: "Multipack (16 pieces)", tag: "Value pack" },
];

const REGIONS = [
  "Greater Accra",
  "Ashanti Region",
  "Eastern Region",
  "Western Region",
  "Central Region",
  "Volta Region",
  "Northern Region",
];

const FACTS = [
  { icon: <Calendar className="w-5 h-5" />, k: "28 Aug 1980", v: "Founded" },
  { icon: <Users className="w-5 h-5" />, k: "≈ 80", v: "Employees" },
  { icon: <Heart className="w-5 h-5" />, k: "70 / 30", v: "Women / Men workforce" },
  { icon: <Factory className="w-5 h-5" />, k: "New Tema", v: "Manufacturing facility" },
];

export default function About() {
  const [openEbenezer, setOpenEbenezer] = useState(false);
  return (
    <div data-testid="about-page" className="bg-white">
      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-faytex-ink text-white">
        <div className="absolute inset-0 -z-0 opacity-40 bg-gradient-to-br from-faytex-pink via-faytex-pinkHover to-faytex-ink" />
        <div className="grain absolute inset-0 -z-0 opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-40">
          <p className="text-xs uppercase tracking-widest text-faytex-pink">Company Overview</p>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl mt-3 tracking-tighter max-w-4xl">
            Fay International Ltd.
          </h1>
          <p className="mt-6 text-white/85 max-w-3xl text-lg leading-relaxed">
            One of Ghana&rsquo;s pioneering manufacturers of menstrual hygiene products. For more
            than four decades we have been committed to improving menstrual health, dignity and
            wellbeing through high-quality sanitary products for women and girls.
          </p>
          <p className="mt-4 text-white/75 max-w-3xl text-sm leading-relaxed">
            Founded in <strong className="text-white">1980</strong> as
            {" "}<strong className="text-white">SAPAD Manufacturing Company</strong>, the business
            was renamed <strong className="text-white">Fay International Limited</strong> in
            {" "}<strong className="text-white">1994</strong> — the name it carries today as the
            home of the Faytex brand.
          </p>
          <p className="mt-10 font-heading text-2xl md:text-3xl text-faytex-pink">
            Faytex — Live Your Life.
          </p>
        </div>
      </section>

      {/* QUICK FACTS BAR */}
      <section className="border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { k: "1980", v: "Founded" },
            { k: "Ghana", v: "Headquartered" },
            { k: "Faytex", v: "Flagship brand" },
            { k: "40+", v: "Years of trust" },
          ].map((s) => (
            <div key={s.k} className="text-center">
              <p className="font-heading text-2xl md:text-3xl text-faytex-pink">{s.k}</p>
              <p className="text-[11px] uppercase tracking-widest text-faytex-muted mt-1">{s.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VISION & MISSION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid md:grid-cols-2 gap-8">
        <div className="rounded-3xl border border-faytex-pinkLight bg-faytex-pinkLight/30 p-8 md:p-10">
          <div className="w-12 h-12 rounded-2xl bg-white text-faytex-pink flex items-center justify-center">
            <Compass className="w-6 h-6" />
          </div>
          <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold mt-5">
            Vision
          </p>
          <h2 className="font-heading text-3xl md:text-4xl mt-2 tracking-tight leading-tight">
            Empowering comfort, dignity and sustainability for every life we touch.
          </h2>
          <p className="mt-5 text-faytex-muted leading-relaxed">
            We envision a world where every woman and girl experiences confidence, comfort and
            dignity through access to high-quality menstrual hygiene products.
          </p>
        </div>
        <div className="rounded-3xl border border-gray-100 bg-faytex-ink text-white p-8 md:p-10">
          <div className="w-12 h-12 rounded-2xl bg-white/10 text-faytex-pink flex items-center justify-center">
            <Target className="w-6 h-6" />
          </div>
          <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold mt-5">
            Mission
          </p>
          <h2 className="font-heading text-3xl md:text-4xl mt-2 tracking-tight leading-tight">
            Superior menstrual hygiene, made in Ghana.
          </h2>
          <p className="mt-5 text-white/80 leading-relaxed">
            To manufacture superior menstrual hygiene solutions that combine innovation, quality,
            affordability and care. Through Faytex, we are committed to improving women&rsquo;s
            health, confidence and wellbeing while supporting a cleaner and more sustainable future.
          </p>
        </div>
      </section>

      {/* ABOUT FAYTEX */}
      <section className="bg-faytex-pinkLight/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center">
          <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">
            About Faytex
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 tracking-tight">
            The flagship of Fay International.
          </h2>
          <p className="mt-8 text-lg text-faytex-muted leading-relaxed">
            Faytex is Fay International&rsquo;s flagship sanitary pad brand. Designed for comfort,
            protection and affordability, Faytex supports women and girls throughout their
            menstrual journey by providing reliable absorbency, comfort and value.
          </p>
          <p className="mt-4 text-faytex-muted leading-relaxed">
            Manufactured in Ghana, Faytex combines quality and accessibility to meet the needs of a
            broad range of consumers.
          </p>
        </div>
      </section>

      {/* EVOLVING WITH WOMEN */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">
          A Brand Evolving With Women
        </p>
        <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 tracking-tight">
          Growing with every generation.
        </h2>
        <div className="mt-8 space-y-4 text-lg text-faytex-muted leading-relaxed">
          <p>
            For decades, Fay International has continued to evolve alongside the needs of Ghanaian
            women and girls. From its early manufacturing roots to the introduction of modern
            winged sanitary pads, the company remains committed to improving comfort, protection
            and accessibility while maintaining the trusted quality consumers have known for
            generations.
          </p>
          <p>
            Today, Faytex serves girls and women at every stage of life — from first periods and
            monthly menstrual care to heavier-flow and maternity-related needs.
          </p>
        </div>
      </section>

      {/* CORE OBJECTIVES */}
      <section className="bg-faytex-ink text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">
              Core Objectives
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 tracking-tight">
              What guides everything we do.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {OBJECTIVES.map((o) => (
              <div
                key={o.title}
                className="rounded-2xl bg-white/5 border border-white/10 p-6 hover:bg-white/10 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-faytex-pink text-white flex items-center justify-center">
                  {o.icon}
                </div>
                <p className="mt-4 font-heading text-lg leading-snug">{o.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT MAKES FAYTEX DIFFERENT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">
            What Makes Faytex Different
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 tracking-tight">
            Seven reasons women choose us.
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {DIFFERENTIATORS.map((d, i) => (
            <div
              key={d}
              className="rounded-2xl border border-gray-100 bg-white p-6 shadow-soft hover:shadow-hover transition-shadow flex items-start gap-4"
            >
              <span className="flex-shrink-0 w-9 h-9 rounded-full bg-faytex-pinkLight text-faytex-pink font-heading text-sm flex items-center justify-center">
                {String(i + 1).padStart(2, "0")}
              </span>
              <p className="text-faytex-ink leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUCT RANGE */}
      <section className="bg-faytex-pinkLight/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">
                Product Range
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 tracking-tight">
                A pad for every stage of the cycle.
              </h2>
            </div>
            <Link
              to="/shop"
              data-testid="about-shop-cta"
              className="inline-flex items-center gap-2 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover text-white px-6 py-3 text-sm font-semibold transition-colors self-start"
            >
              Shop the range
            </Link>
          </div>

          {/* Family product shot */}
          <div className="mt-12 rounded-3xl overflow-hidden shadow-hover bg-white">
            <img
              src="/product-range.webp"
              alt="The full Faytex product range on a soft cream backdrop"
              className="w-full h-auto object-cover"
            />
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PRODUCTS.map((p) => (
              <div key={p.name} className="rounded-3xl bg-white p-7 shadow-soft border border-white">
                <p className="text-[11px] uppercase tracking-widest text-faytex-pink font-semibold">
                  {p.tag}
                </p>
                <h3 className="font-heading text-2xl mt-2 leading-tight">{p.name}</h3>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-faytex-muted italic">
            Designed to provide women with the right level of protection throughout every stage of
            their menstrual cycle.
          </p>
        </div>
      </section>

      {/* MARKET PRESENCE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid md:grid-cols-2 gap-12 items-start">
        <div>
          <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">
            Market Presence
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 tracking-tight">
            Trusted across Ghana &mdash; and growing.
          </h2>
          <p className="mt-6 text-faytex-muted leading-relaxed">
            Faytex products are available across all seven of these regions of Ghana. The company
            continues to explore expansion opportunities across West Africa and neighbouring
            markets.
          </p>
        </div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {REGIONS.map((r) => (
            <li
              key={r}
              className="flex items-center gap-3 rounded-2xl bg-faytex-pinkLight/40 border border-faytex-pinkLight px-5 py-4"
            >
              <MapPin className="w-4 h-4 text-faytex-pink flex-shrink-0" />
              <span className="text-faytex-ink font-medium">{r}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* SOCIAL IMPACT teaser */}
      <section className="bg-faytex-pinkLight/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center">
          <div className="w-14 h-14 rounded-2xl bg-faytex-pink text-white mx-auto flex items-center justify-center">
            <Handshake className="w-7 h-7" />
          </div>
          <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold mt-6">
            Social Impact
          </p>
          <h2 className="font-heading text-3xl md:text-4xl mt-3 tracking-tight">
            Menstrual health for every girl.
          </h2>
          <p className="mt-6 text-faytex-muted leading-relaxed">
            Fay International actively supports menstrual health initiatives through donations and
            partnerships with NGOs, schools and community organisations. These efforts help improve
            access to menstrual hygiene products and contribute to menstrual health education
            across Ghana.
          </p>
          <Link
            to="/impact"
            data-testid="about-impact-link"
            className="inline-flex items-center gap-2 mt-8 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover text-white px-6 py-3 text-sm font-semibold transition-colors"
          >
            See our impact
          </Link>
        </div>
      </section>

      {/* COMPANY FACTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">
            Company Facts
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 tracking-tight">
            By the numbers.
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {FACTS.map((f) => (
            <div key={f.v} className="rounded-3xl border border-gray-100 bg-white p-7 shadow-soft text-center">
              <div className="w-11 h-11 rounded-2xl bg-faytex-pinkLight text-faytex-pink mx-auto flex items-center justify-center">
                {f.icon}
              </div>
              <p className="mt-5 font-heading text-2xl">{f.k}</p>
              <p className="text-xs text-faytex-muted uppercase tracking-widest mt-1">{f.v}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          <div className="rounded-2xl border border-gray-100 bg-faytex-pinkLight/30 p-6">
            <p className="text-[11px] uppercase tracking-widest text-faytex-pink font-semibold">
              Company history
            </p>
            <p className="mt-2 font-heading text-xl leading-tight">Founded as SAPAD in 1980</p>
            <p className="mt-2 text-sm text-faytex-muted leading-relaxed">
              Renamed Fay International Limited in 1994 — the name we still carry today.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpenEbenezer(true)}
            data-testid="ebenezer-card"
            className="text-left rounded-2xl border border-gray-100 bg-faytex-pinkLight/30 p-6 hover:shadow-hover hover:border-faytex-pink transition group focus:outline-none focus:ring-2 focus:ring-faytex-pink"
          >
            <p className="text-[11px] uppercase tracking-widest text-faytex-pink font-semibold">
              Longest-serving employee
            </p>
            <p className="mt-2 font-heading text-xl">Mr. Ebenezer Akrong</p>
            <p className="mt-2 text-xs text-faytex-muted italic group-hover:text-faytex-pink transition-colors">
              Tap to read his story →
            </p>
          </button>
          <div className="rounded-2xl border border-gray-100 bg-faytex-pinkLight/30 p-6">
            <p className="text-[11px] uppercase tracking-widest text-faytex-pink font-semibold">
              Product evolution
            </p>
            <p className="mt-2 text-sm text-faytex-muted leading-relaxed">
              Expansion from maternity pad production to winged sanitary pad production.
            </p>
          </div>
        </div>
      </section>

      {/* FUTURE GOALS / CTA */}
      <section className="bg-faytex-ink text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center">
          <div className="w-14 h-14 rounded-2xl bg-faytex-pink text-white mx-auto flex items-center justify-center">
            <TrendingUp className="w-7 h-7" />
          </div>
          <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold mt-6">
            Future Goals
          </p>
          <h2 className="font-heading text-3xl md:text-5xl tracking-tight mt-3">
            The leading sanitary pad brand in Ghana &mdash; and across Africa.
          </h2>
          <p className="mt-6 text-white/80 leading-relaxed">
            Our five-year ambition: become the leading sanitary pad brand in Ghana and expand
            exports into neighbouring African countries.
          </p>
          <p className="mt-10 font-heading text-2xl md:text-3xl text-faytex-pink">
            Faytex &mdash; Live Your Life.
          </p>
          <Link
            to="/b2b"
            data-testid="about-partner-cta"
            className="inline-flex items-center gap-2 mt-8 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover px-7 py-3.5 text-sm font-semibold transition-colors"
          >
            <Building2 className="w-4 h-4" /> Partner with us
          </Link>
        </div>
      </section>
      {/* Ebenezer Akrong story modal */}
      <Dialog open={openEbenezer} onOpenChange={setOpenEbenezer}>
        <DialogContent
          data-testid="ebenezer-modal"
          className="max-w-3xl p-0 overflow-hidden rounded-3xl"
        >
          <div className="grid md:grid-cols-2 max-h-[85vh] overflow-y-auto">
            <div className="bg-faytex-pinkLight/50 flex items-center justify-center">
              <img
                src="/ebenezer-akrong.webp"
                alt="Mr. Ebenezer Akrong at the Faytex factory"
                className="w-full h-64 md:h-full object-cover"
              />
            </div>
            <div className="p-8 md:p-10">
              <DialogHeader>
                <p className="text-[11px] uppercase tracking-widest text-faytex-pink font-semibold">
                  A Journey of Dedication and Growth
                </p>
                <DialogTitle className="font-heading text-2xl md:text-3xl tracking-tight mt-2">
                  Mr. Ebenezer Akrong
                </DialogTitle>
                <p className="text-xs uppercase tracking-widest text-faytex-muted mt-2">
                  Technical Support Supervisor · Since 1 June 1996
                </p>
              </DialogHeader>
              <div className="mt-6 space-y-4 text-sm text-faytex-muted leading-relaxed">
                <p>
                  Ebenezer Akrong joined Fay on <strong className="text-faytex-ink">1 June 1996</strong>,
                  beginning his journey with the company as a factory hand.
                </p>
                <p>
                  Over the years, through dedication, hard work and a commitment to learning,
                  Ebenezer steadily progressed within the organisation. He went on to become a
                  machine operator and today serves as a{" "}
                  <strong className="text-faytex-ink">Technical Support Supervisor</strong>.
                </p>
                <p>
                  His journey reflects not only his personal growth, but also the opportunities
                  that come with building a career and growing alongside a company. After three
                  decades with Fay, Ebenezer remains an important part of the team and a testament
                  to the value of experience, loyalty and continuous development.
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
